import { apiRequest } from './http';
import type {
  ApiItemResponse,
  ApiListResponse,
  ConnectorDefinitionRecord,
  ConnectorInstanceRecord,
  DestinationPolicyResult,
  GovernanceLifecycleState,
  InboundPolicyRecord,
  MappingVersionRecord,
  MappingRecord,
  PartnerConnectivityValidationRecord,
  PartnerMetadata,
  PartnerRecord,
  PartnerConfigurationVersionRecord,
  As2TransportProfileRecord,
  TemplateRecord,
  ValidationRuleRecord,
} from '../types/api';

type Query = Record<string, string | number | boolean | undefined>;

function queryString(query: Query): string {
  const search = new URLSearchParams();

  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      search.set(key, String(value));
    }
  });

  const serialized = search.toString();
  return serialized ? `?${serialized}` : '';
}

export const configApi = {
  maintenance: {
    demoData: async () => (await apiRequest<ApiItemResponse<{
      partners: Array<{ id: string; name: string; code: string; isActive: boolean; documentCount: number }>;
      as2TransportProfiles: Array<{ id: string; code: string; name: string; outboundEndpoint: string; active: boolean }>;
      safeAction: string;
    }>>('/maintenance/demo-data')).data,
    quarantineDemoData: async () => (await apiRequest<ApiItemResponse<unknown>>('/maintenance/demo-data/quarantine', {
      method: 'POST',
      body: JSON.stringify({}),
    })).data,
  },
  capabilities: {
    documentImplementations: async () => (await apiRequest<ApiItemResponse<Array<{
      documentType: string;
      supportLevel: 'IMPLEMENTED' | 'LIMITED';
      scenario: string;
      limitations: string[];
    }>>>('/capabilities/document-implementations')).data,
  },
  security: {
    monitoringSignals: async () => (await apiRequest<ApiItemResponse<Array<{
      id: string;
      type: 'SUSPICIOUS_ACTIVITY' | 'AUTH_ANOMALY' | 'REPLAY_ATTACK' | 'TENANT_VIOLATION';
      severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
      actor?: string;
      tenant?: string;
      correlationId?: string;
      traceId?: string;
      details: Record<string, unknown>;
      detectedAt: string;
    }>>>('/security/monitoring/signals')).data,
    compliancePosture: async () => (await apiRequest<ApiItemResponse<{
      frameworks: string[];
      controls: Array<{ control: string; status: string; evidence: string }>;
    }>>('/security/compliance/posture')).data,
  },
  partners: {
    metadata: async () => (await apiRequest<ApiItemResponse<PartnerMetadata>>('/partners/metadata')).data,
    list: (query: Query) => apiRequest<ApiListResponse<PartnerRecord>>(`/partners${queryString(query)}`),
    create: async (payload: Omit<PartnerRecord, 'id' | 'createdAt' | 'updatedAt'>) =>
      (await apiRequest<ApiItemResponse<PartnerRecord>>('/partners', { method: 'POST', body: JSON.stringify(payload) })).data,
    update: async (id: string, payload: Partial<Omit<PartnerRecord, 'id' | 'createdAt' | 'updatedAt'>>) =>
      (await apiRequest<ApiItemResponse<PartnerRecord>>(`/partners/${id}`, { method: 'PATCH', body: JSON.stringify(payload) })).data,
    remove: async (id: string) => apiRequest<void>(`/partners/${id}`, { method: 'DELETE' }),
    submit: async (id: string, reason: string, expectedVersion?: number) =>
      (await apiRequest<ApiItemResponse<PartnerRecord>>(`/partners/${id}/submit`, { method: 'POST', body: JSON.stringify({ reason, expectedVersion }) })).data,
    approve: async (id: string, reason: string, expectedVersion?: number) =>
      (await apiRequest<ApiItemResponse<PartnerRecord>>(`/partners/${id}/approve`, { method: 'POST', body: JSON.stringify({ reason, expectedVersion }) })).data,
    reject: async (id: string, reason: string, expectedVersion?: number) =>
      (await apiRequest<ApiItemResponse<PartnerRecord>>(`/partners/${id}/reject`, { method: 'POST', body: JSON.stringify({ reason, expectedVersion }) })).data,
    suspend: async (id: string, reason: string, expectedVersion?: number) =>
      (await apiRequest<ApiItemResponse<PartnerRecord>>(`/partners/${id}/suspend`, { method: 'POST', body: JSON.stringify({ reason, expectedVersion }) })).data,
    reactivate: async (id: string, reason: string, expectedVersion?: number) =>
      (await apiRequest<ApiItemResponse<PartnerRecord>>(`/partners/${id}/reactivate`, { method: 'POST', body: JSON.stringify({ reason, expectedVersion }) })).data,
    connectivity: async (id: string) =>
      (await apiRequest<ApiItemResponse<PartnerConnectivityValidationRecord[]>>(`/partners/${id}/connectivity-tests`)).data,
    testConnectivity: async (id: string, body: { protocol: 'REST' | 'SFTP' | 'WEBHOOK' | 'KAFKA' | 'AS2_GATEWAY'; endpoint?: string; topic?: string }) =>
      (await apiRequest<ApiItemResponse<PartnerConnectivityValidationRecord>>(`/partners/${id}/connectivity-tests`, { method: 'POST', body: JSON.stringify(body) })).data,
    destinationPolicy: async (id: string) =>
      (await apiRequest<ApiItemResponse<DestinationPolicyResult[]>>(`/partners/${id}/destination-policy`)).data,
    versions: async (id: string) =>
      (await apiRequest<{ data: PartnerConfigurationVersionRecord[] }>(`/partners/${id}/versions`)).data,
    rollback: async (id: string, targetVersion: number, expectedVersion: number | undefined, reason: string) =>
      (await apiRequest<ApiItemResponse<PartnerRecord>>(`/partners/${id}/rollback`, {
        method: 'POST',
        body: JSON.stringify({ targetVersion, expectedVersion, reason }),
      })).data,
    listAs2Profiles: async (id: string) => {
      const response = await apiRequest<{ content: As2TransportProfileRecord[] }>(`/partners/${id}/as2-transport-profiles`);
      return response.content;
    },
    saveAs2Profile: async (
      partnerId: string,
      payload: Omit<As2TransportProfileRecord, 'id' | 'name' | 'active'> & { id?: string },
    ) => {
      const path = payload.id
        ? `/partners/${partnerId}/as2-transport-profiles/${payload.id}`
        : `/partners/${partnerId}/as2-transport-profiles`;
      return (await apiRequest<ApiItemResponse<As2TransportProfileRecord>>(path, {
        method: payload.id ? 'PUT' : 'POST',
        body: JSON.stringify(payload),
      })).data;
    },
  },
  inboundPolicies: {
    list: async (query: Query) => {
      const response = await apiRequest<ApiListResponse<Omit<InboundPolicyRecord, 'name'>>>(`/inbound-policies${queryString(query)}`);
      return {
        ...response,
        data: response.data.map(withPolicyName),
      };
    },
    create: async (payload: Omit<InboundPolicyRecord, 'id' | 'name' | 'createdAt' | 'updatedAt'>) =>
      withPolicyName((await apiRequest<ApiItemResponse<Omit<InboundPolicyRecord, 'name'>>>('/inbound-policies', {
        method: 'POST',
        body: JSON.stringify(stripPolicyName(payload)),
      })).data),
    update: async (id: string, payload: Partial<Omit<InboundPolicyRecord, 'id' | 'name' | 'createdAt' | 'updatedAt'>>) =>
      withPolicyName((await apiRequest<ApiItemResponse<Omit<InboundPolicyRecord, 'name'>>>(`/inbound-policies/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(stripPolicyName(payload)),
      })).data),
    remove: async (id: string) => apiRequest<void>(`/inbound-policies/${id}`, { method: 'DELETE' }),
  },
  mappings: {
    list: (query: Query) => apiRequest<ApiListResponse<MappingRecord>>(`/mappings${queryString(query)}`),
    get: async (id: string) => (await apiRequest<ApiItemResponse<MappingRecord>>(`/mappings/${id}`)).data,
    create: async (payload: Omit<MappingRecord, 'id' | 'createdAt' | 'updatedAt'>) =>
      (await apiRequest<ApiItemResponse<MappingRecord>>('/mappings', { method: 'POST', body: JSON.stringify(payload) })).data,
    update: async (id: string, payload: Partial<Omit<MappingRecord, 'id' | 'createdAt' | 'updatedAt'>>) =>
      (await apiRequest<ApiItemResponse<MappingRecord>>(`/mappings/${id}`, { method: 'PATCH', body: JSON.stringify(payload) })).data,
    remove: async (id: string) => apiRequest<void>(`/mappings/${id}`, { method: 'DELETE' }),
    versions: async (id: string) => (await apiRequest<ApiItemResponse<MappingVersionRecord[]>>(`/mappings/${id}/versions`)).data,
    createVersion: async (id: string, payload: { version: string; sourceSchemaRef?: string; ruleDefinition?: Record<string, unknown>; lifecycleState?: Exclude<GovernanceLifecycleState, 'ACTIVE'> }) =>
      (await apiRequest<ApiItemResponse<MappingRecord>>(`/mappings/${id}/versions`, { method: 'POST', body: JSON.stringify(payload) })).data,
    transitionVersion: async (id: string, versionId: string, lifecycleState: GovernanceLifecycleState) =>
      (await apiRequest<ApiItemResponse<MappingRecord>>(`/mappings/${id}/versions/${versionId}/lifecycle`, {
        method: 'PATCH',
        body: JSON.stringify({ lifecycleState }),
      })).data,
  },
  templates: {
    list: (query: Query) => apiRequest<ApiListResponse<TemplateRecord>>(`/templates${queryString(query)}`),
    create: async (payload: Omit<TemplateRecord, 'id' | 'createdAt' | 'updatedAt'>) =>
      (await apiRequest<ApiItemResponse<TemplateRecord>>('/templates', { method: 'POST', body: JSON.stringify(payload) })).data,
    update: async (id: string, payload: Partial<Omit<TemplateRecord, 'id' | 'createdAt' | 'updatedAt'>>) =>
      (await apiRequest<ApiItemResponse<TemplateRecord>>(`/templates/${id}`, { method: 'PATCH', body: JSON.stringify(payload) })).data,
    remove: async (id: string) => apiRequest<void>(`/templates/${id}`, { method: 'DELETE' }),
  },
  validationRules: {
    list: (query: Query) => apiRequest<ApiListResponse<ValidationRuleRecord>>(`/validation-rules${queryString(query)}`),
    create: async (payload: Omit<ValidationRuleRecord, 'id' | 'createdAt' | 'updatedAt'>) =>
      (await apiRequest<ApiItemResponse<ValidationRuleRecord>>('/validation-rules', { method: 'POST', body: JSON.stringify(payload) })).data,
    update: async (id: string, payload: Partial<Omit<ValidationRuleRecord, 'id' | 'createdAt' | 'updatedAt'>>) =>
      (await apiRequest<ApiItemResponse<ValidationRuleRecord>>(`/validation-rules/${id}`, { method: 'PATCH', body: JSON.stringify(payload) })).data,
    remove: async (id: string) => apiRequest<void>(`/validation-rules/${id}`, { method: 'DELETE' }),
  },
  connectorDefinitions: {
    list: (query: Query) => apiRequest<ApiListResponse<ConnectorDefinitionRecord>>(`/connector-definitions${queryString(query)}`),
    create: async (payload: Omit<ConnectorDefinitionRecord, 'id' | 'createdAt' | 'updatedAt'>) =>
      (await apiRequest<ApiItemResponse<ConnectorDefinitionRecord>>('/connector-definitions', { method: 'POST', body: JSON.stringify(payload) })).data,
    update: async (id: string, payload: Partial<Omit<ConnectorDefinitionRecord, 'id' | 'createdAt' | 'updatedAt'>>) =>
      (await apiRequest<ApiItemResponse<ConnectorDefinitionRecord>>(`/connector-definitions/${id}`, { method: 'PATCH', body: JSON.stringify(payload) })).data,
    remove: async (id: string) => apiRequest<void>(`/connector-definitions/${id}`, { method: 'DELETE' }),
  },
  connectorInstances: {
    list: (query: Query) => apiRequest<ApiListResponse<ConnectorInstanceRecord>>(`/connector-instances${queryString(query)}`),
    create: async (payload: Omit<ConnectorInstanceRecord, 'id' | 'createdAt' | 'updatedAt'>) =>
      (await apiRequest<ApiItemResponse<ConnectorInstanceRecord>>('/connector-instances', { method: 'POST', body: JSON.stringify(payload) })).data,
    update: async (id: string, payload: Partial<Omit<ConnectorInstanceRecord, 'id' | 'createdAt' | 'updatedAt'>>) =>
      (await apiRequest<ApiItemResponse<ConnectorInstanceRecord>>(`/connector-instances/${id}`, { method: 'PATCH', body: JSON.stringify(payload) })).data,
    remove: async (id: string) => apiRequest<void>(`/connector-instances/${id}`, { method: 'DELETE' }),
  },
};

function withPolicyName(policy: Omit<InboundPolicyRecord, 'name'>): InboundPolicyRecord {
  return {
    ...policy,
    name: policy.flowKey ? `${policy.partnerId} / ${policy.flowKey}` : `${policy.partnerId} / ${policy.protocol}`,
  };
}

function stripPolicyName<T extends Record<string, unknown>>(policy: T): Omit<T, 'name'> {
  const { name: _name, ...payload } = policy;
  return payload;
}
